<script setup lang="ts">
interface Props {
  label: string
  active?: boolean
  disabled?: boolean
  variant?: 'primary' | 'secondary' | 'neutral'
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  active: false,
  disabled: false,
  variant: 'primary',
  size: 'medium',
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

const isSingleNumber = computed(() => {
  return /^\d$/.test(props.label)
})

function handleClick() {
  if (!props.disabled) {
    emit('click')
  }
}
</script>

<template>
  <div
    class="button-tag"
    :class="[
      `button-tag-${variant}`,
      `button-tag-${size}`,
      {
        'button-tag-active': active,
        'button-tag-disabled': disabled,
        'button-tag-circular': isSingleNumber,
      },
    ]"
    @click="handleClick"
  >
    {{ label }}
  </div>
</template>

<style scoped lang="scss">
.button-tag {
  @apply flex items-center gap-4 py-nano px-micro justify-center;
  @apply rounded-full border border-solid transition-all duration-200 cursor-pointer select-none;
  @apply font-body-medium-regular bg-neutral-high-light text-neutral-low-medium border-neutral-high-light;
  @apply whitespace-nowrap;

  &.button-tag-active {
    @apply bg-transparent border border-solid border-feedback-positive-pure text-feedback-positive-dark;

    &.button-tag-primary {
      @apply border-feedback-positive-pure text-feedback-positive-dark;
    }

    &.button-tag-secondary {
      @apply border-brand-secondary-pure text-brand-secondary-dark;
    }

    &.button-tag-neutral {
      @apply border-neutral-low-dark text-neutral-low-dark;
    }
  }

  &.button-tag-disabled {
    @apply cursor-not-allowed opacity-50;

    &:not(.button-tag-active) {
      @apply bg-neutral-high-light text-neutral-low-medium border-none;
      @apply border border-solid;
    }

    &.button-tag-active {
      @apply bg-transparent border border-solid border-feedback-positive-pure text-feedback-positive-dark;

    }
  }

  &:hover:not(.button-tag-disabled) {
    &:not(.button-tag-active) {
      @apply bg-neutral-high-medium;
    }

    &.button-tag-active {
      @apply opacity-80;
    }
  }

  &.button-tag-circular {
    @apply w-10 h-10 p-2 min-w-10;
    @apply flex items-center justify-center;

    &.button-tag-small {
      @apply w-8 h-8 p-1 min-w-8;
    }

    &.button-tag-medium {
      @apply w-10 h-10 p-2 min-w-10;
    }

    &.button-tag-large {
      @apply w-12 h-12 p-3 min-w-12;
    }
  }
}
</style>
